import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CreditCard, MessageCircle, ShieldCheck, X } from 'lucide-react';
import { useSubscription } from '../lib/hooks/useSubscription';
import { useStripeCheckout } from '../lib/hooks/useStripeCheckout';
import { useAuth } from '../lib/auth';

/**
 * Blocco totale dopo la scadenza del grace period di 14gg su un pagamento
 * Stripe fallito (le org billing_model='reseller' sono escluse a monte da
 * isPastDue/isInGracePeriod: paga il partner, non questa org).
 *
 * Il CTA apre il Customer Portal Stripe (dominio esterno stripe.com), non una
 * route interna: aprendo un URL esterno non c'è il rischio "come pago se sono
 * bloccato" che si avrebbe reindirizzando a /settings sotto la stessa modale.
 */
export function PastDueBlockedModal() {
  const subscription = useSubscription();
  const { openPortal, loading } = useStripeCheckout();
  const { isSuperAdmin } = useAuth();
  const [dismissed, setDismissed] = useState(false);

  if (dismissed && isSuperAdmin) return null;
  if (subscription.loading || !subscription.organization) return null;
  if (!subscription.isPastDue || subscription.isInGracePeriod) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.25 }}
        className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-[#37352F]/50 backdrop-blur-sm"
      >
        <motion.div
          initial={{ opacity: 0, y: 12, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
          className="relative w-full max-w-[440px] rounded-2xl bg-white shadow-2xl border border-[rgba(55,53,47,0.09)] overflow-hidden"
        >
          {isSuperAdmin && (
            <button
              onClick={() => setDismissed(true)}
              className="absolute top-4 right-4 z-10 p-1.5 rounded-lg text-[#787774] hover:text-[#37352F] hover:bg-[#F7F6F3] transition-colors"
              title="Chiudi (solo superadmin)"
            >
              <X className="w-4 h-4" />
            </button>
          )}

          <div className="px-7 pt-8 pb-6">
            <div className="w-11 h-11 rounded-xl bg-[#0f6cbd]/10 flex items-center justify-center mb-5">
              <CreditCard className="w-5 h-5 text-[#0f6cbd]" strokeWidth={2} />
            </div>

            <h2 className="text-[21px] font-bold text-[#37352F] leading-tight tracking-[-0.01em] mb-2">
              Un ultimo passo per continuare
            </h2>
            <p className="text-[14px] text-[#787774] leading-relaxed mb-5">
              L'ultimo addebito non è andato a buon fine — capita, una carta scaduta o un
              contrattempo momentaneo. Aggiorna il metodo di pagamento e riprendi esattamente
              da dove avevi lasciato.
            </p>

            <ul className="space-y-2 mb-6">
              {[
                'Lead, clienti e offerte: tutto intatto',
                'Riattivazione immediata, nessuna configurazione da rifare',
                'Bastano due minuti sul portale sicuro di Stripe',
              ].map((item) => (
                <li key={item} className="flex items-start gap-2.5 text-[13px] text-[#37352F]">
                  <ShieldCheck className="w-4 h-4 text-[#0f6cbd] flex-shrink-0 mt-0.5" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>

            <button
              onClick={() => openPortal()}
              disabled={loading}
              className="w-full py-3 rounded-xl bg-[#0f6cbd] hover:bg-[#0a5ba8] text-white text-[14px] font-semibold transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
            >
              <CreditCard className="w-4 h-4" />
              {loading ? 'Apertura in corso…' : 'Aggiorna metodo di pagamento'}
            </button>

            <a
              href="https://wa.me/393932787927?text=Ciao%2C%20ho%20un%20problema%20con%20il%20pagamento%20del%20mio%20abbonamento%20Flusia"
              target="_blank"
              rel="noopener noreferrer"
              className="mt-3 w-full py-2.5 rounded-xl text-[13px] font-medium text-[#787774] hover:text-[#37352F] hover:bg-[#F7F6F3] transition-colors flex items-center justify-center gap-1.5"
            >
              <MessageCircle className="w-3.5 h-3.5" />
              Serve aiuto? Scrivici su WhatsApp
            </a>
          </div>

          <div className="px-7 py-3.5 bg-[#F7F6F3] border-t border-[rgba(55,53,47,0.09)]">
            <p className="text-center text-[11px] text-[#787774]">
              Il tuo account e tutti i tuoi dati restano al sicuro.
            </p>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
